import React, { useEffect, useRef, useState, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { getSearchModalClasses, dialogSx, searchInputSx, resultsListSx, resultItemSx } from './SearchModal.styles';
import { useTheme } from '@mui/material/styles';
import useDebounce from 'utils/debounce';
import services from 'services';
import DescriptionIcon from '@mui/icons-material/DescriptionRounded';
import { IconsForFileType, IconsForVklType } from 'store/utils/knowledgeGraphIcons';

/**
 * Gets the appropriate icon component for a search result
 * @param {Object} result - The search result object with { vklId, filePath, isInSharedKg, type }
 * @returns {React.ComponentType} - The icon component
 */
export function getResultIcon(result) {
  // Use type from backend if available
  if (result.type) {
    const type = result.type;

    // Check VKL types first (exact matches for better accuracy)
    if (IconsForVklType[type]) {
      return IconsForVklType[type];
    }

    // Check file types with exact matches first
    if (IconsForFileType[type]) {
      return IconsForFileType[type];
    }
  }

  // Fallback: use generic file icon for both concepts and files
  return DescriptionIcon;
}

/** Search modal component
 *
 * - Shows loading indicator after a small delay (300ms)
 * - Allows navigation with arrow keys and selection with Enter
 * - Uses MUI Dialog for proper modal behavior
 * - Debounces search queries by 300ms
 */
const SearchModal = ({ placeholderText, kgId, onResultSelected, isOpen, onClose }) => {
  const { classes } = getSearchModalClasses();
  const theme = useTheme();

  const [query, setQuery] = useState('');
  // List of results, undefined if no results are fetched yet and for the empty query
  const [results, setResults] = useState(undefined);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const [error, setError] = useState(null);

  // Keep track of the previously selected result's ID so we can
  // preserve selection if it is still present in the newly fetched list
  // This is only updated when the user explicitly selects an result using the arrow keys
  const prevSelectedResultIdRef = useRef(null);

  // For the loading delay
  const loadingDelayRef = useRef(null);

  // Debounce the search query
  const debouncedQuery = useDebounce(query, 300);

  // Input ref to focus the input automatically when the palette opens
  const inputRef = useRef(null);

  // Ref for the results container to handle scrolling
  const resultsContainerRef = useRef(null);
  // Refs for individual result items to handle scrolling
  const resultItemRefs = useRef([]);

  // Whenever the palette is opened or closed, focus the input and reset state
  useEffect(() => {
    if (isOpen) {
      // Use requestAnimationFrame to ensure DOM is ready, then focus immediately
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      });
    }
    // reset the state
    prevSelectedResultIdRef.current = null;
    resultItemRefs.current = [];
    setQuery('');
    setResults(undefined);
    setSelectedIndex(0);
    setShowLoadingIndicator(false);
    setError(null);
  }, [isOpen]);

  // Effect for scrolling when selectedIndex changes
  useEffect(() => {
    if (!isOpen || !results || results.length === 0 || selectedIndex < 0) return;

    // Use requestAnimationFrame to ensure we measure after DOM updates
    requestAnimationFrame(() => {
      const selectedElement = resultItemRefs.current[selectedIndex];
      if (selectedElement && resultsContainerRef.current) {
        const containerRect = resultsContainerRef.current.getBoundingClientRect();
        const elementRect = selectedElement.getBoundingClientRect();

        // Calculate if the element is out of view (no margins)
        const isAboveView = elementRect.top < containerRect.top;
        const isBelowView = elementRect.bottom > containerRect.bottom;

        if (isAboveView || isBelowView) {
          selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    });
  }, [selectedIndex, isOpen, results]);

  // Function to search files and concepts
  const searchFilesAndConcepts = useCallback(
    async searchQuery => {
      if (!kgId || !searchQuery.trim()) {
        return [];
      }

      let response;
      try {
        response = await services.searchFilesAndConcepts(kgId, searchQuery);
      } catch (error) {
        console.error('Search error:', error);
        throw error;
      }

      if (response.success) {
        return response.data.results.map(result => ({
          id: result.vklId || result.filePath,
          label: result.vklId || result.filePath,
          data: result
        }));
      }

      throw new Error('Search failed');
    },
    [kgId]
  );

  // Function to handle fetching results
  const handleFetchResults = useCallback(
    async searchQuery => {
      if (!isOpen || !searchQuery.trim()) {
        setResults(undefined);
        setShowLoadingIndicator(false);
        setError(null);
        return;
      }

      // Set up loading delay to prevent flicker
      loadingDelayRef.current = setTimeout(() => {
        setShowLoadingIndicator(true);
      }, 300);

      try {
        const newResults = await searchFilesAndConcepts(searchQuery);

        // Clear loading delay if it hasn't fired yet
        if (loadingDelayRef.current) {
          clearTimeout(loadingDelayRef.current);
        }

        setShowLoadingIndicator(false);
        setError(null);
        setResults(Array.isArray(newResults) ? newResults : []);

        // Clear old refs to prevent memory leaks
        resultItemRefs.current = [];

        // Preserve selection if possible
        if (prevSelectedResultIdRef.current) {
          const indexOfPrev = newResults.findIndex(act => act.id === prevSelectedResultIdRef.current);
          setSelectedIndex(indexOfPrev !== -1 ? indexOfPrev : 0);
        } else {
          setSelectedIndex(0);
        }
      } catch (err) {
        console.error('Error fetching results:', err);

        if (loadingDelayRef.current) {
          clearTimeout(loadingDelayRef.current);
        }

        setShowLoadingIndicator(false);
        setResults(undefined);
        setError('An error occurred.');
      }
    },
    [searchFilesAndConcepts, isOpen]
  );

  // Fetch results when debounced query changes
  useEffect(() => {
    handleFetchResults(debouncedQuery);

    return () => {
      // Cleanup loading delay on unmount or query change
      if (loadingDelayRef.current) {
        clearTimeout(loadingDelayRef.current);
      }
    };
  }, [debouncedQuery, handleFetchResults]);

  // Keyboard constants
  const KEYS = {
    ARROW_UP: 'ArrowUp',
    ARROW_DOWN: 'ArrowDown',
    ENTER: 'Enter',
    TAB: 'Tab'
  };

  // Handle keyboard navigation inside the palette
  const handleKeyDown = e => {
    const { key } = e;
    const navigationKeys = [KEYS.ARROW_DOWN, KEYS.ARROW_UP, KEYS.ENTER, KEYS.TAB];

    // Prevent default for navigation keys
    if (navigationKeys.includes(key)) {
      e.preventDefault();
    }

    if (!results || results.length === 0) {
      // If there are no results, just prevent default for navigation keys
      return;
    }

    if (key === KEYS.ARROW_DOWN) {
      setSelectedIndex(prev => {
        const newIndex = (prev + 1) % results.length;
        prevSelectedResultIdRef.current = results[newIndex].id;
        return newIndex;
      });
    } else if (key === KEYS.ARROW_UP) {
      setSelectedIndex(prev => {
        const newIndex = (prev - 1 + results.length) % results.length;
        prevSelectedResultIdRef.current = results[newIndex].id;
        return newIndex;
      });
    } else if (key === KEYS.ENTER) {
      // Jump to the selected result
      onResultSelected(results[selectedIndex]);
    } else if (key === KEYS.TAB) {
      // Put the selected result as query
      setQuery(results[selectedIndex].label);
    }
  };

  const handleItemClick = (result, index) => {
    if (!showLoadingIndicator) {
      setSelectedIndex(index);
      onResultSelected(result);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={dialogSx}
      PaperProps={{
        elevation: 8
      }}
      disableAutoFocus={false}
      disableEnforceFocus={false}
      disableRestoreFocus={false}
    >
      <DialogContent className={classes.dialogContent}>
        <TextField
          inputRef={inputRef}
          sx={searchInputSx}
          placeholder={placeholderText || 'Search for definitions or files...'}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          variant="outlined"
          fullWidth
          autoComplete="off"
          autoFocus
          InputProps={{
            endAdornment: showLoadingIndicator && <CircularProgress size={16} />
          }}
          inputProps={{
            'aria-label': 'Search for definitions or files'
          }}
        />

        {/* Error message */}
        {error && <Box className={classes.errorMessage}>{error}</Box>}

        {/* Results list */}
        <List ref={resultsContainerRef} sx={resultsListSx} disablePadding>
          {results && results.length === 0 && <Box className={classes.noResults}>No results found</Box>}
          {results &&
            results.map((result, index) => {
              const isSelected = index === selectedIndex;
              const IconComponent = getResultIcon(result.data);

              return (
                <ListItem
                  key={result.id}
                  ref={el => {
                    resultItemRefs.current[index] = el;
                  }}
                  sx={resultItemSx(theme, isSelected, showLoadingIndicator)}
                  onClick={() => handleItemClick(result, index)}
                  disablePadding
                >
                  <ListItemIcon className={classes.resultIcon}>
                    <IconComponent />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <div className={classes.primaryText}>
                        <span>{result.data?.name || result.label}</span>
                        {result.data?.vklId && <span className={classes.vklId}>{result.data.vklId}</span>}
                      </div>
                    }
                    secondary={result.data?.description}
                  />
                </ListItem>
              );
            })}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;
